/**
 * @file trendChartMath.ts
 * @brief Pure numbers for `TrendChart` — totals, half-window change, axis domain.
 *
 * @details
 * Kept out of the React file so the headline math can be tested without
 * rendering Recharts. Postgres aggregates sometimes arrive as numeric
 * strings; anything that is not a finite number is skipped.
 *
 * @author DES
 * @date 28-09-2026
 */

export interface TrendSummary {
  /** Sum of finite points in the series. */
  total: number;
  formatted: string;
  /**
   * Percent change of the later half of the range against the earlier half.
   * Null when the series is too short, or the earlier half is zero (a
   * percent off zero reads as infinity and is not a real change).
   */
  deltaPct: number | null;
}

export function finiteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    if (value.trim() === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (Array.isArray(value) && value.length > 0) return finiteNumber(value[0]);
  return null;
}

/** Later-half sum versus earlier-half sum, as a percent. */
export function halfWindowDelta(values: readonly number[]): number | null {
  if (values.length < 4) return null;
  const mid = Math.floor(values.length / 2);
  let earlier = 0;
  let later = 0;
  for (let i = 0; i < values.length; i += 1) {
    if (i < mid) earlier += values[i] ?? 0;
    else later += values[i] ?? 0;
  }
  if (earlier === 0) return null;
  return ((later - earlier) / Math.abs(earlier)) * 100;
}

export function formatTrendDelta(deltaPct: number): string {
  const abs = Math.abs(deltaPct);
  const digits = abs >= 10 || abs === 0 ? 0 : 1;
  const body = abs.toFixed(digits);
  if (deltaPct > 0) return `+${body}%`;
  if (deltaPct < 0) return `-${body}%`;
  return "0%";
}

export function summarizeTrend(
  rows: ReadonlyArray<Record<string, unknown>>,
  dataKey: string,
  format?: (value: number) => string,
): TrendSummary {
  const values: number[] = [];
  for (const row of rows) {
    const n = finiteNumber(row[dataKey]);
    if (n != null) values.push(n);
  }
  const total = values.reduce((sum, n) => sum + n, 0);
  return {
    total,
    formatted: format ? format(total) : total.toLocaleString("en-GB"),
    deltaPct: halfWindowDelta(values),
  };
}

/**
 * Keep a zero baseline when the series stays non-negative, and leave
 * headroom so the stroke and the end dot are not clipped. Negative series
 * (profit) extend below zero instead of being pinned.
 */
export function trendYDomain(dataMin: number, dataMax: number): [number, number] {
  const min = Number.isFinite(dataMin) ? dataMin : 0;
  const max = Number.isFinite(dataMax) ? dataMax : 0;
  const base = min < 0 ? min : 0;
  const top = max <= base ? base + 1 : max;
  const span = top - base || 1;
  const paddedMin = base < 0 ? base - span * 0.08 : 0;
  const paddedMax = top + span * 0.14;
  return paddedMax > paddedMin ? [paddedMin, paddedMax] : [paddedMin, paddedMin + 1];
}
